import { BucketId, IAgingAgencia, IAgingData } from './types';
import { agencyName, BUCKETS_POR_VENCER, BUCKETS_VENCIDO } from './constants';

/** USD 12.345 — sin decimales: en un informe de cartera los centavos son ruido. */
export function usd(value: number): string {
	return `USD ${new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(value)}`;
}

/** Igual que `usd` pero devuelve '—' en cero, para que la tabla respire. */
export function usdOrDash(value: number): string {
	return value === 0 ? '—' : usd(value);
}

export function formatFecha(iso: string | null | undefined): string {
	if (!iso) return '—';
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
	if (!m) return iso;
	return `${m[3]}/${m[2]}/${m[1]}`;
}

export function hoyISO(): string {
	const d = new Date();
	const mm = String(d.getMonth() + 1).padStart(2, '0');
	const dd = String(d.getDate()).padStart(2, '0');
	return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Severidad de un tramo de vencido, para el semáforo. Cuanto más viejo, más
 * fuerte: es lo que hace que el ojo vaya primero a lo que más duele.
 */
export function severidadBucket(bucket: BucketId): 'error' | 'warning' | 'info' | 'default' {
	switch (bucket) {
		case 'V_45_MAS':
		case 'V_31_45':
			return 'error';
		case 'V_16_30':
			return 'warning';
		case 'V_8_15':
		case 'V_1_7':
			return 'info';
		default:
			return 'default';
	}
}

/** Color del importe vencido de un cliente según su tramo más viejo con saldo. */
export function colorVencido(buckets: Record<BucketId, number>): string {
	if (buckets.V_45_MAS > 0) return 'error.dark';
	if (buckets.V_31_45 > 0) return 'error.main';
	if (buckets.V_16_30 > 0) return 'warning.dark';
	if (buckets.V_8_15 > 0 || buckets.V_1_7 > 0) return 'warning.main';
	return 'text.primary';
}

/**
 * El informe a CSV. Cobranzas lo pide siempre: es lo que pegan en su planilla
 * de seguimiento. Se exportan los tramos abiertos, que es lo que no se puede
 * reconstruir a ojo desde la pantalla.
 */
export function agingToCsv(data: IAgingData): string {
	const head = [
		'Agencia',
		'Cliente',
		'Documento',
		'Saldo de cobranza',
		'Vencido',
		...BUCKETS_VENCIDO.map((b) => `Vencido ${b.corto}`),
		'Por vencer',
		...BUCKETS_POR_VENCER.map((b) => `Por vencer ${b.corto}`),
		'Pendiente de facturar',
		'Saldo a favor',
		'Diferencia',
		'Órdenes',
		'Facturas',
	];

	const filas: (string | number)[][] = [];
	for (const ag of data.agencias) {
		for (const c of ag.clientes) {
			filas.push([
				agencyName(ag.agencyId),
				c.nombre,
				c.documento ?? '',
				c.saldoCobranza,
				c.vencidoTotal,
				...BUCKETS_VENCIDO.map((b) => c.buckets[b.id] ?? 0),
				c.porVencerTotal,
				...BUCKETS_POR_VENCER.map((b) => c.buckets[b.id] ?? 0),
				c.pendienteFacturar,
				c.saldoAFavor,
				c.diferencia,
				c.ordenes,
				c.facturas,
			]);
		}
		if (ag.multiCliente) {
			filas.push([
				agencyName(ag.agencyId),
				'(órdenes con más de un cliente facturable)',
				'',
				ag.multiCliente.saldoCobranza,
				0,
				...BUCKETS_VENCIDO.map(() => 0),
				0,
				...BUCKETS_POR_VENCER.map(() => 0),
				ag.multiCliente.pendienteFacturar,
				ag.multiCliente.saldoAFavor,
				0,
				ag.multiCliente.ordenes,
				0,
			]);
		}
	}

	const escape = (v: string | number): string => {
		const s = String(v);
		return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
	};
	return [head, ...filas].map((r) => r.map(escape).join(';')).join('\n');
}

export function descargarCsv(contenido: string, nombre: string): void {
	// BOM para que Excel abra bien los acentos.
	const blob = new Blob([`\ufeff${contenido}`], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = nombre;
	a.click();
	URL.revokeObjectURL(url);
}

/** Ordena los clientes de una agencia por lo que más duele primero. */
export function clientesOrdenados(agencia: IAgingAgencia): IAgingAgencia['clientes'] {
	return [...agencia.clientes].sort((a, b) => {
		if (b.vencidoTotal !== a.vencidoTotal) return b.vencidoTotal - a.vencidoTotal;
		if (b.porVencerTotal !== a.porVencerTotal) return b.porVencerTotal - a.porVencerTotal;
		return b.saldoCobranza - a.saldoCobranza;
	});
}
